import { isOnline } from "../connectivity";
import axios from "axios";
const DB_NAME = "puestify_pos";
/** v7: asegura outbox_cajas aunque la DB ya hubiera quedado en v6 sin el store. */
const DB_VERSION = 7;

function ensurePosStores(db) {
  if (!db.objectStoreNames.contains("device")) {
    db.createObjectStore("device", { keyPath: "id" });
  }
  if (!db.objectStoreNames.contains("catalog")) {
    db.createObjectStore("catalog", { keyPath: "key" });
  }
  if (!db.objectStoreNames.contains("config")) {
    db.createObjectStore("config", { keyPath: "key" });
  }
  if (!db.objectStoreNames.contains("outbox")) {
    const ob = db.createObjectStore("outbox", {
      keyPath: "client_mutation_id",
    });
    ob.createIndex("sync_status", "sync_status", { unique: false });
    ob.createIndex("created_at", "created_at", { unique: false });
  }
  if (!db.objectStoreNames.contains("meta")) {
    db.createObjectStore("meta", { keyPath: "key" });
  }
  if (!db.objectStoreNames.contains("categorias")) {
    db.createObjectStore("categorias", { keyPath: "key" });
  }
  if (!db.objectStoreNames.contains("outbox_productos")) {
    db.createObjectStore("outbox_productos", { keyPath: "id" });
  }
  if (!db.objectStoreNames.contains("outbox_categorias")) {
    db.createObjectStore("outbox_categorias", { keyPath: "id" });
  }
  if (!db.objectStoreNames.contains("outbox_mesas")) {
    const om = db.createObjectStore("outbox_mesas", {
      keyPath: "client_mutation_id",
    });
    om.createIndex("sync_status", "sync_status", { unique: false });
    om.createIndex("mesa_id", "mesa_id", { unique: false });
    om.createIndex("created_at", "created_at", { unique: false });
  }
  if (!db.objectStoreNames.contains("outbox_cajas")) {
    const oc = db.createObjectStore("outbox_cajas", {
      keyPath: "client_mutation_id",
    });
    oc.createIndex("sync_status", "sync_status", { unique: false });
    oc.createIndex("created_at", "created_at", { unique: false });
    oc.createIndex("tipo", "tipo", { unique: false });
  }
}

export function openPosDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (ev) => {
      ensurePosStores(ev.target.result);
    };
  });
}

export async function idbGet(storeName, key) {
  const db = await openPosDb();
  if (!db.objectStoreNames.contains(storeName)) {
    return undefined;
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function idbPut(storeName, value) {
  const db = await openPosDb();
  if (!db.objectStoreNames.contains(storeName)) {
    throw new Error(`IndexedDB store ausente: ${storeName}. Recargá la página.`);
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).put(value);
    tx.oncomplete = () => resolve(value);
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbGetAll(storeName) {
  const db = await openPosDb();
  if (!db.objectStoreNames.contains(storeName)) {
    return [];
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly");
    const req = tx.objectStore(storeName).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

export async function idbDelete(storeName, key) {
  const db = await openPosDb();
  if (!db.objectStoreNames.contains(storeName)) {
    return false;
  }
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite");
    tx.objectStore(storeName).delete(key);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * IndexedDB solo acepta valores clonables (structured clone). Los proxies
 * reactivos de Vue, funciones o getters revientan con DataCloneError, así que
 * normalizamos a JSON plano antes de guardar datos puros.
 * OJO: no usar en el store `device`, que guarda un CryptoKey (no serializable).
 */
export function toStorable(value) {
  if (value == null) return value;
  if (typeof value !== "object") return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (_) {
    return value;
  }
}

export async function cachePosSnapshot(key, value) {
  return idbPut("config", {
    key,
    value: toStorable(value),
    cached_at: new Date().toISOString(),
  });
}

export async function getCachedPosSnapshot(key) {
  const row = await idbGet("config", key);
  return row ? row.value : null;
}

export async function cachePosCatalog(key, value) {
  return idbPut("catalog", {
    key,
    value: toStorable(value),
    cached_at: new Date().toISOString(),
  });
}

export async function getCachedPosCatalog(key) {
  const row = await idbGet("catalog", key);
  return row ? row.value : null;
}

/**
 * Reserves a persistent per-device folio and enqueues the immutable sale in one
 * IndexedDB transaction. Printing deliberately happens after this commits.
 */
export async function allocateFolioAndEnqueue({
  deviceUuid,
  deviceCode,
  entry,
  negocio = null,
}) {
  const db = await openPosDb();
  const negocioKey =
    negocio ||
    (entry && entry.payload && entry.payload.usuario_negocio) ||
    null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(["device", "outbox"], "readwrite");
    const deviceStore = tx.objectStore("device");
    const outboxStore = tx.objectStore("outbox");
    const req = deviceStore.get("local");
    let saved;

    req.onerror = () => reject(req.error);
    req.onsuccess = () => {
      const device = req.result || {
        id: "local",
        device_uuid: deviceUuid,
        codigo: deviceCode,
        blocked: false,
        enrollments: {},
      };
      const enrollments = { ...(device.enrollments || {}) };
      // Migrar legado a slot del negocio.
      if (
        negocioKey &&
        !enrollments[negocioKey] &&
        device.usuario_negocio === negocioKey
      ) {
        enrollments[negocioKey] = {
          codigo: device.codigo || null,
          nombre: device.nombre || null,
          certificate: device.certificate || null,
          cert_vence_at: device.cert_vence_at || null,
          folio_seq: Number(device.folio_seq) || 0,
        };
      }
      const slot = (negocioKey && enrollments[negocioKey]) || {};
      const nextSeq = Math.max(
        0,
        Number(
          (slot.folio_seq != null ? slot.folio_seq : negocioKey ? 0 : device.folio_seq) || 0,
        ) + 1,
      );
      // No reutilizar código de otra cuenta si el slot del negocio aún no existe.
      const codigo = String(
        slot.codigo ||
          deviceCode ||
          (!negocioKey ? device.codigo : null) ||
          "LOCAL",
      ).toUpperCase();
      const ticketFolio = `POS-${codigo}-${String(nextSeq).padStart(6, "0")}`;
      if (negocioKey) {
        enrollments[negocioKey] = {
          ...slot,
          codigo,
          folio_seq: nextSeq,
        };
      }
      deviceStore.put({
        ...device,
        id: "local",
        device_uuid: device.device_uuid || deviceUuid,
        codigo,
        folio_seq: nextSeq,
        usuario_negocio: negocioKey || device.usuario_negocio || null,
        enrollments,
      });
      saved = toStorable({
        ...entry,
        device_uuid: device.device_uuid || deviceUuid,
        ticket_folio: ticketFolio,
      });
      outboxStore.put(saved);
    };
    tx.oncomplete = () => resolve(saved);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () =>
      reject(tx.error || new Error("No se pudo guardar la venta offline."));
  });
}


//funciones offline para categorias
export async function cacheCategorias(key, value) {
  return idbPut("categorias", {
    key,
    value: toStorable(value),
    cached_at: new Date().toISOString(),
  });
}

/** Obtiene el array de categorías desde la caché */
export async function getCachedCategorias(key) {
  const row = await idbGet("categorias", key);
  return row ? row.value : null;
}

/** Agrega una sola categoría a la caché existente cuando estás offline */
export async function agregarCategoriaOffline(usuario, nuevaCategoria) {

  const key = `categorias:${usuario}`;

  const categoriaConTempid = toStorable({
    ...nuevaCategoria,
    id:`temp_cat_${Date.now()}`,
    _pendingSync:true,
    creado_en:new Date().toISOString(),
    usuario:usuario
  })

  
  const categoriasActuales = (await getCachedCategorias(key)) || [];

  const categoriasActualizadas = [categoriaConTempid, ...categoriasActuales];

  await cacheCategorias(key,categoriasActualizadas);

  
  await idbPut("outbox_categorias", categoriaConTempid);

  // 3. Volvemos a guardar el array completo
  return categoriaConTempid;
}

export async function obtenerCategoriasCatalogo(usuario) {
  const cacheKey = `categorias:${usuario}`;

  // 1. Categorías en caché (filtramos las temporales viejas)
  const categoriasCached = (await getCachedCategorias(cacheKey)) || [];
  const categoriasSinPendientes = categoriasCached.filter(c => !c._pendingSync);

  // 2. Pendientes del Outbox de categorías
  const outboxCategorias = (await idbGetAll("outbox_categorias")) || [];
  const misPendientes = outboxCategorias.filter(c => c.usuario === usuario);

  // 3. Unimos: Pendientes + Oficiales
  return [...misPendientes, ...categoriasSinPendientes];
}

export async function syncOutboxCategorias(usuario) {
  if (!isOnline()) return { syncedCount: 0 };

  // 1. Buscamos la cola de categorías pendientes
  const queue = await idbGetAll("outbox_categorias");

  if (!queue || queue.length === 0) return { syncedCount: 0 };

  let syncedCount = 0;

  for (const cat of queue) {
    try {
      const payload = { ...cat };
      delete payload.id;
      delete payload._pendingSync;
      delete payload.creado_en;
      delete payload.usuario;

      await axios.post("/nuevaCategoria", payload);

      // 2. Si se envió con éxito, se borra de la bandeja de salida local
      await idbDelete("outbox_categorias", cat.id);

      syncedCount++;
    } catch (error) {
      console.error("Error sincronizando categoría offline:", cat, error);
    }
  }

  // 3. Si subimos algo, refrescamos la caché con los datos reales del servidor
  if (syncedCount > 0) {
    try {
      // CAMBIA ESTO por la ruta real donde pides tus categorías
      const { data } = await axios.get("/categorias", { params: { usuario } });
      if (Array.isArray(data)) {
        await cacheCategorias(`categorias:${usuario}`, data);
      }
    } catch (e) {
      console.error("No se pudo refrescar la caché de categorías tras sincronizar:", e);
    }
  }

  return { syncedCount };
}